import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { isEqual } from 'lodash';
import AgGridReact from '../../../common/AgGrid/AgGridReact.jsx';
import { gridThemeNoBorder, condensedTheme } from '../../../common/AgGrid/gridTheme.js';

import { buildColumnDefs } from '../../../datagrid/hooks/useDynamicCols.jsx';
import {
  selectDatasourceCols,
  selectDatasourceRows,
  selectCombinedFilterModel,
  setWidgetEffectiveCols,
} from '../../redux/datasourceSlice.js';
import styles from './Widget.module.scss';
import AutoGroupValueRenderer from '../../../datagrid/AutoGroupValueRenderer.jsx';

export const DatasourceContext = createContext({ filteredRows: [], cols: [], datasourceId: null });

// Stable default — avoids creating a new object reference on every render when group_model is absent.
const EMPTY_GROUP_MODEL = { rowGroups: [], valueCols: [], pivotCols: [], pivotMode: false };
export const useDatasourceContext = () => useContext(DatasourceContext);

/** Walk from a group node up to the root, collecting each level's field→key pair. */
function collectGroupKeys(node) {
  const keys = {};
  let n = node;
  while (n?.field) { keys[n.field] = n.key; n = n.parent; }
  return keys;
}

/**
 * Pivot mode: AG Grid's virtual pivot result columns and their aggregated values per group
 * are only available via the group node API (getPivotResultColumns / node.aggData).
 * We still read aggData here because pivot result columns have no stable colId in our
 * datasource schema — manual aggregation is not feasible for pivot. This branch is therefore
 * limited to widgets where cross-filter is not expected to update pivot values in real time.
 */
function extractPivotResult(api, cols, rowGroups) {
  const pivotResultCols = api.getPivotResultColumns?.() ?? [];
  const rows = [];
  const noRowGroups = !rowGroups || rowGroups.length === 0;

  api.forEachNodeAfterFilterAndSort(node => {
    if (noRowGroups) {
      // No row groups: all nodes are leaf data rows. The root node (node.parent of any
      // level-0 node) holds the total pivot aggregation in aggData — read it once.
      if (!rows.length && node.parent?.aggData) {
        rows.push({ ...node.parent.aggData });
      }
    } else if (node.leafGroup) {
      rows.push({ ...node.aggData, ...collectGroupKeys(node) });
    }
  });

  const newPivotCols = pivotResultCols.map(col => ({
    accessor: col.getColId(),
    name: col.getColDef().headerName ?? col.getColId(),
    data_type: 'decimal',
  }));
  const groupColIds = new Set((rowGroups ?? []).map(g => g.colId));
  const groupCols = (cols ?? []).filter(c => groupColIds.has(c.accessor));
  return { rows, cols: [...groupCols, ...newPivotCols] };
}

// Used by extractGroupedRows: manual aggregation on filtered leaf rows ensures cross-filters
// are reflected in grouped output (group node aggData may lag behind the advanced filter).
function applyAggFunc(values, aggFunc) {
  const nums = values.filter(v => v != null && v !== '' && !isNaN(Number(v))).map(Number);
  if (!nums.length) return null;
  switch (aggFunc) {
    case 'min':   return Math.min(...nums);
    case 'max':   return Math.max(...nums);
    case 'avg':   return nums.reduce((a, b) => a + b, 0) / nums.length;
    case 'count': return values.length;
    default:      return nums.reduce((a, b) => a + b, 0); // sum and fallback
  }
}

/** Row groups + aggregation: manually aggregate filtered leaf rows per group. */
function extractGroupedRows(api, rowGroups, valueCols) {
  const leafRows = [];
  api.forEachNodeAfterFilterAndSort(node => { if (!node.group) leafRows.push(node.data); });

  const groupMap = new Map();
  leafRows.forEach(row => {
    const key = rowGroups.map(g => String(row?.[g.colId] ?? '')).join(' ');
    if (!groupMap.has(key)) {
      const entry = { _rows: [] };
      rowGroups.forEach(g => { entry[g.colId] = row?.[g.colId]; });
      groupMap.set(key, entry);
    }
    groupMap.get(key)._rows.push(row);
  });

  return Array.from(groupMap.values()).map(({ _rows, ...keys }) => {
    const result = { ...keys };
    valueCols.forEach(({ colId, aggFunc }) => {
      result[colId] = applyAggFunc(_rows.map(r => r?.[colId]), aggFunc ?? 'sum');
    });
    return result;
  });
}

/** Aggregation without row groups: single grand-total row from the root node's aggData. */
function extractGrandTotal(api, cols, valueCols) {
  let rootAggData = null;
  api.forEachNodeAfterFilterAndSort(node => {
    if (!rootAggData && node.parent?.aggData) rootAggData = node.parent.aggData;
  });
  if (!rootAggData) return null;
  const aggColIds = new Set(valueCols.map(v => v.colId));
  return {
    rows: [{ ...rootAggData }],
    cols: (cols ?? []).filter(c => aggColIds.has(c.accessor)),
  };
}

/** No grouping or aggregation: flat leaf rows as-is. */
function extractLeafRows(api) {
  const rows = [];
  api.forEachNodeAfterFilterAndSort(node => { if (!node.group) rows.push(node.data); });
  return rows;
}

/**
 * Builds AG Grid column definitions from datasource cols, applying grouping and aggregation
 * from the groupModel config.
 */
function buildColumnDefsWithGrouping(cols, groupModel) {
  const base = buildColumnDefs(cols ?? [], { editMode: false });
  const { rowGroups = [], valueCols = [], pivotCols = [] } = groupModel ?? {};
  return base.map(def => {
    const rowGroup = rowGroups.find(g => g.colId === def.field);
    const valueCol = valueCols.find(v => v.colId === def.field);
    const pivotCol = pivotCols.find(p => p.colId === def.field);
    return {
      ...def,
      ...(rowGroup ? { rowGroup: true, hide: true } : {}),
      ...(valueCol ? { aggFunc: valueCol.aggFunc } : {}),
      ...(pivotCol ? { pivot: true } : {}),
    };
  });
}

/**
 * DatasourceEngineGrid — single AG Grid instance that serves as:
 * 1. The filter/aggregation engine for widgets (always rendered, hidden when not displaying)
 * 2. The inline data display grid (visible when grid view toggled on)
 * 3. React context provider of filteredRows for child widget components
 *
 * @param {number} widgetId
 * @param {boolean} displayGrid - whether grid is currently toggled on (for toggleable mode)
 * @param {string} gridMode - 'toggleable' | 'left' | 'right' | 'above' | 'below'
 * @param {number} gridSize - percentage size when in fixed mode
 * @param {boolean} canDisplay - whether this widget supports showing the grid (widgetsWithGridView)
 * @param {React.ReactNode} children - the widget component(s)
 */
function DatasourceEngineGrid({ widgetId, displayGrid, gridMode, gridSize, canDisplay, children }) {
  const dispatch = useDispatch();
  const datasourceId = useSelector(
    state => state.dashboard.widgets[widgetId]?.data_datasource?.id
  );
  const rows = useSelector(selectDatasourceRows(datasourceId, widgetId));
  const cols = useSelector(selectDatasourceCols(datasourceId, widgetId));
  // isEqual prevents re-render loops when cross-filters produce a new object with the same content.
  const combinedFilterModel = useSelector(selectCombinedFilterModel(widgetId, datasourceId), isEqual);
  // EMPTY_GROUP_MODEL is a stable constant — using an inline `?? {}` literal here would create a new
  // reference every render and trigger an infinite useSelector re-render loop when group_model is absent.
  const groupModel = useSelector(
    state => state.dashboard.widgets[widgetId]?.data_datasource?.group_model ?? EMPTY_GROUP_MODEL
  );

  const [filteredRows, setFilteredRows] = useState(rows ?? []);
  const [effectiveCols, setEffectiveCols] = useState(cols ?? []);

  // Sync effectiveCols to Redux so settings sidebar components (outside this context tree) can read them.
  useEffect(() => {
    dispatch(setWidgetEffectiveCols({ widgetId, cols: effectiveCols }));
  }, [dispatch, widgetId, effectiveCols]);
  const gridRef = useRef(null);
  // Mount the advanced filter bar into a hidden element so it doesn't appear in the widget UI
  const hiddenFilterBarRef = useRef(null);

  const columnDefs = useMemo(
    () => buildColumnDefsWithGrouping(cols, groupModel),
    [cols, groupModel]
  );

  const extractFilteredRows = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) return;

    const { rowGroups = [], valueCols = [] } = groupModel ?? {};
    if (api.isPivotMode()) {
      const { rows, cols: newCols } = extractPivotResult(api, cols, rowGroups);
      setFilteredRows(rows);
      setEffectiveCols(newCols);
      return;
    }

    if (valueCols.length > 0 && rowGroups.length === 0) {
      const result = extractGrandTotal(api, cols, valueCols);
      if (result) { setFilteredRows(result.rows); setEffectiveCols(result.cols); }
      return;
    }

    if (rowGroups.length > 0 && valueCols.length > 0) {
      setFilteredRows(extractGroupedRows(api, rowGroups, valueCols));
      setEffectiveCols(cols ?? []);
      return;
    }

    setFilteredRows(extractLeafRows(api));
    setEffectiveCols(cols ?? []);
  }, [cols, groupModel]);

  const onGridReady = useCallback((params) => {
    params.api.setGridOption('advancedFilterParent', hiddenFilterBarRef.current);
    // Apply filter before extracting so the first pass sees filtered data.
    // setAdvancedFilterModel is synchronous — filter is applied before it returns.
    params.api.setAdvancedFilterModel(combinedFilterModel ?? null);
    extractFilteredRows();
  }, [extractFilteredRows, combinedFilterModel]);

  // Apply combined filter model (advanced filter format) whenever it changes,
  // then re-extract so filteredRows in context always reflects the applied filter.
  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    api.setAdvancedFilterModel(combinedFilterModel ?? null);
    extractFilteredRows();
  }, [combinedFilterModel, extractFilteredRows]);

  const autoGroupColumnDef = useMemo(() => ({
    width: 180,
    cellRendererParams: { innerRenderer: AutoGroupValueRenderer },
  }), []);

  // Determine whether the grid container should be visible
  const gridVisible = canDisplay && (gridMode !== 'toggleable' || displayGrid);

  const gridContainerStyle = gridVisible
    ? { width: '100%', height: '100%' }
    : { position: 'absolute', width: 0, height: 0, overflow: 'hidden', pointerEvents: 'none', visibility: 'hidden' };

  const agGridEl = (
    <div className='grid-theme datasource-engine-grid' style={gridContainerStyle}>
      <div ref={hiddenFilterBarRef} style={{ display: 'none' }} />
      <AgGridReact
        ref={gridRef}
        rowData={rows}
        columnDefs={columnDefs}
        onSortChanged={extractFilteredRows}
        onFilterChanged={extractFilteredRows}
        onRowDataUpdated={extractFilteredRows}
        onGridReady={onGridReady}
        enableAdvancedFilter={true}
        pivotMode={groupModel.pivotMode ?? false}
        grandTotalRow={(groupModel.valueCols ?? []).length > 0 ? 'bottom' : undefined}
        groupDisplayType='multipleColumns'
        autoGroupColumnDef={autoGroupColumnDef}
        theme={gridThemeNoBorder.withParams(condensedTheme)}
        rowHeight={20}
        popupParent={document.body}
        suppressNoRowsOverlay
      />
    </div>
  );

  const content = useMemo(() => {
    // Toggleable: show grid OR widget (not both), but always render the hidden grid engine
    if (!canDisplay || gridMode === 'toggleable') {
      return (
        <>
          {agGridEl}
          {(!canDisplay || !displayGrid) && children}
        </>
      );
    }
    // Fixed position: show both grid and widget side by side
    return (
      <div
        className={`${styles['widget-grid-container']} ${styles[gridMode]}`}
        style={{ '--grid-size': `${gridSize ?? 50}%` }}
      >
        <div className={styles['widget-content']}>{children}</div>
        <div className={styles['widget-grid-content']}>{agGridEl}</div>
      </div>
    );
  }, [canDisplay, gridMode, displayGrid, gridSize, children, agGridEl]);
  return (
    <DatasourceContext.Provider value={{ filteredRows, cols: effectiveCols, datasourceId }}>
      {content}
    </DatasourceContext.Provider>
  );
}

export default DatasourceEngineGrid;
